import { Link } from 'react-router-dom';
import { Shield, Phone, Clock, CheckCircle, ChevronRight, Radio, Zap, Bell, MessageSquare } from 'lucide-react';
import { SEO } from '../components/SEO';

// Client-provided images
const IMAGES = {
  k9Handler: "https://customer-assets.emergentagent.com/job_surfside-guard/artifacts/jp5zo9ku_IMG_1273.jpeg",
  executive: "https://customer-assets.emergentagent.com/job_surfside-guard/artifacts/ouzxb1wd_IMG_1274.jpeg",
  k9Dog: "https://customer-assets.emergentagent.com/job_surfside-guard/artifacts/0h56cfcn_IMG_1272.jpeg",
  hero: "https://images.unsplash.com/photo-1759160897588-6d8f84c59bb5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBidWlsZGluZyUyMGV4dGVyaW9yJTIwbmlnaHQlMjBzZWN1cml0eSUyMGRhcmt8ZW58MHx8fHwxNzcwNTEzNjYzfDA&ixlib=rb-4.1.0&q=85",
  drone: "https://images.unsplash.com/photo-1692527021975-23fdcc191482?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxESkklMjBNYXRyaWNlJTIwMzBUJTIwZW50ZXJwcmlzZSUyMGRyb25lfGVufDB8fHx8MTc2OTk3MDgxN3ww&ixlib=rb-4.1.0&q=85",
  dispatch: "https://images.unsplash.com/photo-1719400471588-575b23e27bd7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxjb21wdXRlciUyMG1vbml0b3JzJTIwZGFyayUyMHJvb20lMjBzY3JlZW5zfGVufDB8fHx8MTc2OTk2OTY0Nnww&ixlib=rb-4.1.0&q=85",
  patrol: "https://images.unsplash.com/photo-1764565690092-04131ab352c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGd1YXJkJTIwZmxhc2hsaWdodCUyMG5pZ2h0JTIwcHJvcGVydHl8ZW58MHx8fHwxNzcwNTEzOTE2fDA&ixlib=rb-4.1.0&q=85",
  armedGuard: "https://images.unsplash.com/photo-1618371690240-e0d46eead4b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMG9mZmljZXIlMjBibGFjayUyMHN1aXQlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzcwNTE0MzAyfDA&ixlib=rb-4.1.0&q=85",
};

const services = [
  {
    title: 'Executive Protection',
    description: 'Professional close protection for VIPs and executives',
    image: IMAGES.executive,
    link: '/services#executive-protection'
  },
  {
    title: 'K9 Security',
    description: 'Trained K9 units for property and event security',
    image: IMAGES.k9Handler,
    link: '/services#k9-security'
  },
  {
    title: 'Drone Surveillance',
    description: 'DJI Matrice 30T with thermal imaging & 48MP camera',
    image: IMAGES.drone,
    link: '/services#drone-surveillance'
  },
  {
    title: 'Armed & Unarmed',
    description: 'Licensed security officers for any situation',
    image: IMAGES.armedGuard,
    link: '/services#armed-security'
  },
  {
    title: 'Patrol Services',
    description: 'Mobile patrol units protecting your property',
    image: IMAGES.patrol,
    link: '/services#patrol-services'
  },
  {
    title: 'CCTV Monitoring',
    description: '24/7 camera installation and monitoring',
    image: IMAGES.dispatch,
    link: '/services#cctv-monitoring'
  },
];

const features = [
  {
    icon: <Clock className="h-8 w-8" />,
    title: '24/7 Dispatch',
    description: 'Round-the-clock dispatch center ready to respond to any security need'
  },
  {
    icon: <Shield className="h-8 w-8" />,
    title: 'PPO Licensed',
    description: 'Fully licensed by the State of California - PPO #15982'
  },
  {
    icon: <CheckCircle className="h-8 w-8" />,
    title: 'Bonded & Insured',
    description: 'Complete coverage for your peace of mind'
  },
];

export const Home = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="home-page">
      <SEO 
        title="Elite Security Services in Southern California" 
        description="Surfside Patrol provides professional security services including executive protection, K9 units, drone surveillance, armed guards, and 24/7 monitoring. PPO #15982. Call 714-588-2460."
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt="Security patrol"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/50 to-[#0A0A0A]" />
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <div className="max-w-3xl">
            <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-4 animate-fadeInUp">
              PPO #15982 • Licensed & Insured
            </p>
            <h1 className="text-5xl md:text-7xl font-bold uppercase tracking-tighter text-white mb-6 font-['Barlow_Condensed'] animate-fadeInUp animation-delay-100">
              Elite Security<br />
              <span className="text-[#F5C518]">Services</span>
            </h1>
            <p className="text-lg md:text-xl text-zinc-400 leading-relaxed mb-8 animate-fadeInUp animation-delay-200">
              Comprehensive security solutions for Southern California. Executive protection, 
              K9 units, drone surveillance, and 24/7 monitoring.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp animation-delay-300">
              <Link
                to="/contact"
                className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#E5B508] transition-all duration-300 text-center"
                data-testid="hero-get-quote"
              >
                <span>Get Free Quote</span>
              </Link>
              <a
                href="tel:714-588-2460"
                className="inline-flex items-center justify-center border border-zinc-700 text-white hover:border-[#F5C518] hover:text-[#F5C518] font-bold uppercase tracking-wider px-8 py-4 transition-all duration-300"
                data-testid="hero-call-now"
              >
                <Phone className="h-5 w-5 mr-2" />
                <span>714-588-2460</span>
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-zinc-600 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-[#F5C518] rounded-full" />
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-4">
              Our Services
            </p>
            <h2 className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-white font-['Barlow_Condensed']">
              Security Solutions
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link
                key={service.title}
                to={service.link}
                className={`group relative bg-[#121212] border border-zinc-800 overflow-hidden hover:border-[#F5C518]/50 transition-all duration-500 animate-fadeInUp animation-delay-${index * 100}`}
                data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold uppercase tracking-wide text-white mb-2 font-['Barlow_Condensed'] group-hover:text-[#F5C518] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-zinc-500 text-sm">
                    {service.description}
                  </p>
                  <div className="mt-4 flex items-center text-[#F5C518] text-sm font-semibold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Learn More <ChevronRight className="h-4 w-4 ml-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-block border border-zinc-700 text-white hover:border-[#F5C518] hover:text-[#F5C518] font-bold uppercase tracking-wider px-8 py-4 transition-all duration-300"
              data-testid="view-all-services"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32 bg-[#121212]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-4">
              Why Choose Us
            </p>
            <h2 className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-white font-['Barlow_Condensed']">
              Trusted Protection
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`text-center p-8 border border-zinc-800 bg-[#0A0A0A]/50 animate-fadeInUp animation-delay-${index * 100}`}
                data-testid={`feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F5C518]/10 text-[#F5C518] mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold uppercase tracking-wide text-white mb-4 font-['Barlow_Condensed']">
                  {feature.title}
                </h3>
                <p className="text-zinc-500">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* K9 & Drone Showcase */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="showcase-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-4">
                Advanced Security
              </p>
              <h2 className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-white font-['Barlow_Condensed'] mb-6">
                K9 Units & Drone Technology
              </h2>
              <p className="text-zinc-400 text-lg leading-relaxed mb-8">
                Our K9 units are trained to the highest standards for property protection and threat 
                detection. Combined with our DJI Matrice 30T drones, we provide comprehensive aerial 
                and ground surveillance capabilities.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Trained security K9 handlers',
                  'Thermal and visual drone cameras',
                  'Real-time monitoring and response',
                  'Property perimeter protection'
                ].map((item) => (
                  <li key={item} className="flex items-center text-zinc-400">
                    <CheckCircle className="h-5 w-5 text-[#F5C518] mr-3 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/services"
                className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#E5B508] transition-all duration-300"
                data-testid="learn-more-services"
              >
                <span>Learn More</span>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src={IMAGES.k9Handler}
                alt="K9 Handler"
                className="w-full aspect-[3/4] object-cover border border-zinc-800"
              />
              <div className="space-y-4">
                <img
                  src={IMAGES.drone}
                  alt="Matrice 30T Security Drone"
                  className="w-full aspect-video object-cover border border-zinc-800"
                />
                <img
                  src={IMAGES.k9Dog}
                  alt="Security K9"
                  className="w-full aspect-video object-cover border border-zinc-800"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Automated Dispatch Showcase */}
      <section className="py-24 md:py-32 bg-[#121212]" data-testid="dispatch-showcase-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Visual / Feature Display */}
            <div className="order-2 lg:order-1">
              <div className="bg-[#0A0A0A] border border-zinc-800 p-8 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#F5C518] to-[#F5C518]/20" />
                <p className="text-[#F5C518] text-xs font-mono uppercase tracking-widest mb-6">
                  // Security Ops · SMS Dispatch
                </p>
                <h3 className="text-2xl font-bold uppercase tracking-tight text-white mb-8 font-['Barlow_Condensed']">
                  Every Guard. Every Shift.<br /><span className="text-[#F5C518]">A Text Away.</span>
                </h3>

                {/* Timeline Steps */}
                <div className="space-y-6 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-16 h-10 bg-[#F5C518]/10 border border-[#F5C518]/30 flex items-center justify-center">
                      <span className="text-[#F5C518] text-xs font-mono font-bold">T-10m</span>
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm uppercase tracking-wide font-['Barlow_Condensed']">Auto Ping</p>
                      <p className="text-zinc-500 text-sm">Guard receives automated shift confirmation text</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-16 h-10 bg-[#F5C518]/10 border border-[#F5C518]/30 flex items-center justify-center">
                      <span className="text-[#F5C518] text-xs font-mono font-bold">T+5m</span>
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm uppercase tracking-wide font-['Barlow_Condensed']">Escalation</p>
                      <p className="text-zinc-500 text-sm">No response triggers supervisor notification</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-16 h-10 bg-[#F5C518]/10 border border-[#F5C518]/30 flex items-center justify-center">
                      <span className="text-[#F5C518] text-xs font-mono font-bold">T+15m</span>
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm uppercase tracking-wide font-['Barlow_Condensed']">No-Show Protocol</p>
                      <p className="text-zinc-500 text-sm">Dispatch activates replacement and incident logging</p>
                    </div>
                  </div>
                </div>

                <p className="text-zinc-600 text-xs font-mono uppercase tracking-wider">
                  Channel Encrypted · UTC Synced
                </p>
              </div>
            </div>

            {/* Text Content */}
            <div className="order-1 lg:order-2">
              <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-4">
                Automated Dispatch Technology
              </p>
              <h2 className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-white font-['Barlow_Condensed'] mb-6">
                Smart Dispatch.<br />Zero Missed Posts.
              </h2>
              <p className="text-zinc-400 text-lg leading-relaxed mb-8">
                Our proprietary automated dispatch system ensures every guard confirms every shift — 
                no exceptions. Automated SMS confirmations, real-time escalation protocols, and 
                instant no-show replacement keep your property protected around the clock.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  { icon: <MessageSquare className="h-5 w-5" />, text: 'Automated SMS shift confirmations' },
                  { icon: <Bell className="h-5 w-5" />, text: 'Real-time escalation when guards don\'t respond' },
                  { icon: <Zap className="h-5 w-5" />, text: 'Instant no-show detection and replacement' },
                  { icon: <Radio className="h-5 w-5" />, text: '24/7 dispatch center coordination' },
                ].map((item) => (
                  <li key={item.text} className="flex items-center text-zinc-400">
                    <span className="text-[#F5C518] mr-3 flex-shrink-0">{item.icon}</span>
                    {item.text}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#E5B508] transition-all duration-300"
                data-testid="dispatch-learn-more"
              >
                <span>Learn More</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#121212] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-20">
          <img
            src={IMAGES.dispatch}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-4">
            Get Started Today
          </p>
          <h2 className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-white font-['Barlow_Condensed'] mb-6">
            Request a Free Security Assessment
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto mb-8">
            Contact us today to discuss your security needs. Our team will provide a 
            comprehensive assessment and customized security solution.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/contact"
              className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#E5B508] transition-all duration-300"
              data-testid="cta-get-quote"
            >
              <span>Get Free Quote</span>
            </Link>
            <a
              href="tel:714-588-2460"
              className="inline-flex items-center justify-center border border-zinc-700 text-white hover:border-[#F5C518] hover:text-[#F5C518] font-bold uppercase tracking-wider px-8 py-4 transition-all duration-300"
              data-testid="cta-call-now"
            >
              <Phone className="h-5 w-5 mr-2" />
              <span>Call 714-588-2460</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
